<template>
    <div
        class="d-flex justify-content-start quote-chat-message"
        :class="{
            'quote-chat-message-self': senderIsSelf,
            'quote-chat-message-other': !senderIsSelf,
            'quote-chat-message-ai': fromAI,
        }"
    >
        <div
            v-if="fromAI"
            class="quote-chat-message-avatar d-flex justify-content-center align-items-center"
        >
            <i class="fa-regular fa-robot"></i>
        </div>
        <img
            v-else-if="senderAvatar"
            class="quote-chat-message-avatar"
            :src="senderAvatar"
            :alt="senderName"
        />
        <div
            v-else
            class="quote-chat-message-avatar d-flex justify-content-center align-items-center"
        >
            <i class="fa-regular fa-user"></i>
        </div>

        <div class="quote-chat-message-box rounded p-2 w-100">
            <div class="d-flex justify-content-between align-items-start">
                <strong>{{ fromAI ? "Vera" : senderName }}:</strong>
                <small v-if="formattedTimestamp">{{
                    formattedTimestamp
                }}</small>
            </div>
            <div v-if="fromAI" v-html="formattedMessage"></div>
            <div v-else>
                {{ formattedMessage }}
            </div>
            <a
                v-if="attachment?.available && attachmentIsImage"
                :href="useDocumentViewer ? '#' : attachment.url"
                :target="useDocumentViewer ? null : '_blank'"
                :rel="useDocumentViewer ? null : 'noopener noreferrer'"
                :data-toggle="useDocumentViewer ? 'modal' : null"
                :data-target="useDocumentViewer ? `#${viewerId}` : null"
                class="quote-chat-attachment-image d-block mt-2"
            >
                <img
                    class="quote-chat-attachment-thumbnail rounded"
                    :src="attachment.url"
                    :alt="attachment.name"
                    loading="lazy"
                />
            </a>
            <a
                v-else-if="attachment?.available"
                :href="useDocumentViewer ? '#' : attachment.url"
                :target="useDocumentViewer ? null : '_blank'"
                :rel="useDocumentViewer ? null : 'noopener noreferrer'"
                :data-toggle="useDocumentViewer ? 'modal' : null"
                :data-target="useDocumentViewer ? `#${viewerId}` : null"
                class="quote-chat-attachment-card d-flex align-items-center rounded border p-2 mt-2"
            >
                <i class="fa-regular fa-file-pdf mr-2" aria-hidden="true"></i>
                <span>
                    <span class="d-block">{{ attachment.name }}</span>
                    <small class="text-muted">{{ attachmentSize }}</small>
                </span>
            </a>
            <small v-else-if="attachment" class="d-block text-muted mt-2">
                Bilagan är inte längre tillgänglig
            </small>
        </div>
        <Teleport v-if="useDocumentViewer" to="body">
            <documents-modal
                :id="viewerId"
                :quote-key="quoteKey"
                :print-url="route('quoteDocuments.print', { quoteKey, id: attachment.document_id })"
                :main-pdf-name="attachment.name"
                :main-pdf-url="attachment.url"
                :main-pdf-id="attachment.document_id"
                :main-pdf-is-pdf="attachment.mime === 'application/pdf'"
                :is-signed="true"
                :is-post-sale="true"
                :defer-reader="true"
            />
        </Teleport>
    </div>
</template>
<style lang="scss" scoped>
.quote-chat-message {
    &.quote-chat-message-self {
        flex-direction: row-reverse;

        .quote-chat-message-avatar {
            margin: 2px 0 2px 0.5em;
        }
        .quote-chat-message-box {
            background: hsl(220, 51%, 90%);
        }
    }

    &.quote-chat-message-ai {
        .quote-chat-message-box {
            background: #7c6c94 !important;
            color: white !important;
        }
    }

    &.quote-chat-message-other {
        flex-direction: row;

        .quote-chat-message-avatar {
            margin: 2px 0.5em 0 2px;
        }
        .quote-chat-message-box {
            background: hsl(220, 6%, 90%);
        }
    }

    .quote-chat-attachment-image {
        width: min(100%, 240px);
        aspect-ratio: 4 / 3;
    }

    .quote-chat-attachment-thumbnail {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    .quote-chat-attachment-card {
        white-space: normal;
    }

    .quote-chat-message-avatar {
        width: 1.4em;
        height: 1.4em;

        border-radius: 50%;
        background-color: #efefef;

        i {
            font-size: 0.8em;
        }
    }

    .quote-chat-message-box {
        overflow-wrap: anywhere;
        white-space: pre-line;
    }
}
</style>
<script setup>
import { computed } from "vue";
import { marked } from "marked";
import { v4 as uuidv4 } from "uuid";
import { route } from "ziggy";
import DocumentsModal from "../quotes/modals/documents.vue";
import { formatFileSize } from "./file-size";
const DATETIME_FORMATTER_OLD = new Intl.DateTimeFormat("default", {
    dateStyle: "short",
    timeStyle: "short",
});
const DATETIME_FORMATTER_TODAY = new Intl.DateTimeFormat("default", {
    timeStyle: "short",
});
const props = defineProps({
    quoteKey: String,
    isCustomer: Boolean,
    disablePdfViewer: Boolean,
    senderName: String,
    senderAvatar: String,
    senderIsSelf: Boolean,
    fromAI: Boolean,
    message: String,
    attachment: {
        type: Object,
        default: null,
    },
    timestamp: {
        type: Date,
        default: null,
    },
});
const attachmentIsImage = computed(() =>
    ["image/png", "image/jpeg", "image/webp"].includes(props.attachment?.mime)
);
const viewerId = `chat-document-${uuidv4()}`;
const useDocumentViewer = computed(() =>
    props.isCustomer && props.quoteKey && props.attachment?.available && !props.disablePdfViewer
);
const attachmentSize = computed(() => formatFileSize(props.attachment?.size ?? 0));
const formattedTimestamp = computed(() => {
    if (!props.timestamp) {
        return null;
    }
    if (props.timestamp.toDateString() === new Date().toDateString()) {
        return DATETIME_FORMATTER_TODAY.format(props.timestamp);
    }
    return DATETIME_FORMATTER_OLD.format(props.timestamp);
});
const formattedMessage = computed(() => {
    if (!props.fromAI) {
        return props.message;
    }
    return marked.parse(props.message);
});
</script>
